<template>
  <div class="text-sm space-y-4 md:space-y-6" data-test-id="product-reviews">
    <h3 v-if="showHeading" class="mb-6 w-full text-left title-3 ">
      {{ $t.reviews }}
    </h3>
    <template v-if="data?.rollup?.reviewCount">
      <section class="grid gap-8" :class="{ 'lg:cols-2': isAiSummaryAvailable }">
        <section class="w-full space-y-8" :class="snapshotSectionClass">
          <div
            class="flex items-center gap-2"
            data-test-id="review-snapshot-section"
          >
            <div
              class="mx-a f-col items-center px-8"
              data-test-id="review-snapshot-average-reviews"
            >
              <p v-if="data?.rollup?.averageRating" class="display-3">
                {{ data?.rollup?.averageRating }}
              </p>
              <vf-rating v-if="data?.rollup?.averageRating" class="mb-2" :value="data?.rollup?.averageRating" />
              <base-button class="caption-1" @click="PanelProductReviews.open()">
                {{ replaceAll(
                  $pluralize($t.numberOfReviews, data?.rollup?.reviewCount),
                  { reviewsCount: data?.rollup?.reviewCount }) }}
              </base-button>
            </div>
            <product-reviews-rating-breakdown
              v-if="$feature.showReviewBreakdown"
              :rating-histogram="data?.rollup?.ratingHistogram?.map((reviewCount, i) => ({
                rating: i + 1,
                reviewCount,
              })).reverse()"
              :reviews-count="data?.rollup?.reviewCount"
              @filter-by-rating="PanelProductReviews.open({ rating: $event })"
            />
          </div>
          <product-reviews-fit-rating
            v-if="fitRatings.length && $feature.showReviewFitSlider"
            :fit-ratings
          />
          <div class="f-col gap-2 md:flex-row">
            <vf-button class="grow" @click="PanelProductReviews.open()">
              {{ $t.readAllReviews }}
            </vf-button>
            <vf-button class="grow" :variant="writeAReviewCta?.variant" @click="openWriteReviewModal">
              {{ $t.writeAReview }}
            </vf-button>
          </div>
        </section>
        <product-reviews-ai-summary v-if="isAiSummaryAvailable">
          {{ data.rollup.reviewSummary }}
        </product-reviews-ai-summary>
      </section>
      <product-reviews-carousel
        v-if="$feature.showReviewsImageCarousel && data?.rollup?.media?.length"
        :media="data.rollup.media"
        :product-id
      />
      <template v-if="$feature.showReviewPreview">
        <vf-notification v-if="error" :dismissible="false" type="error">
          {{ error?.cause?.errorId === ApiErrorCode.REVIEWS_MAX_LENGTH
            ? $t.reviewsTooManyFiltersError
            : $t.reviewsError }}
        </vf-notification>
        <div v-else class="grid gap-8" :class="data?.reviews?.length > 1 ? 'md:cols-2' : 'cols-1'">
          <template v-if="pending">
            <product-reviews-skeleton v-for="key in 2" :key />
          </template>
          <product-reviews-item
            v-else
            v-for="review in data?.reviews?.slice(0, 2)"
            :key="review.reviewId"
            :product-id
            :review
          />
        </div>
      </template>
    </template>
    <div v-else class="f-col items-center gap-10 text-center" data-test-id="product-reviews-empty-state">
      <div class="f-col items-center gap-3">
        <p aria-hidden="true" class="display-2">
          -/-
        </p>
        <p class="caption-1">
          {{ $t.reviewsNone }}
        </p>
      </div>
      <vf-button
        class="min-h-11 min-w-32 <md:w-full"
        variant="tertiary"
        @click="openWriteReviewModal"
      >
        {{ $t.writeAReview }}
      </vf-button>
    </div>

    <panel-review-write />
    <panel-product-reviews class-content="md:w-128" v-bind="$props" />
  </div>
</template>

<script lang="ts" setup>
import type { ProductReviewsData } from '#types/components/product/reviews'
import { ApiErrorCode } from '#root/enums/api'

const { showHeading = true, data, error, productId } = defineProps<{
  currency?: string
  data?: ProductReviewsData | null
  error: any
  pending: boolean
  productId: string
  showHeading?: boolean
}>()

const { $gtm, $pluralize, $t, $feature } = useNuxtApp()
const { writeAReviewCta } = useAppConfig().pages.pdp.reviews
const { useBazaarvoice, openWriteForm } = useReviewProvider()

const PanelReviewWrite = createDialog('review-write')
const PanelProductReviews = createPanel('product-reviews')
const fitRatings = computed(() => data?.rollup?.properties?.filter(({ display_type }) => display_type === 'histogram') || [])
const isAiSummaryAvailable = computed(() => $feature.showReviewAiSummary && data?.rollup?.reviewSummary)
// match the width of a single review card in the 2-col grid below (gap-8 = 2rem, so half gap = 1rem)
const snapshotSectionClass = computed(() => {
  if (isAiSummaryAvailable.value) return 'mx-a'
  return $feature.showReviewPreview && (data?.reviews?.length ?? 0) > 1 ? 'md:max-w-[calc(50%-1rem)]' : ''
})

const openWriteReviewModal = () => {
  $gtm.push('pdpPage.onWriteReview')
  if (useBazaarvoice) {
    closeDialogs()
    openWriteForm(productId)
    return
  }
  PanelReviewWrite.keep().open({
    pageId: data?.reviewsPageId || data?.pageId,
    productId,
  })
}
</script>
